<template>
  <div class="gas-law-pro-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$PV = nRT$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真演示区 -->
      <div class="simulation-stage">
        <div class="hud-panel">
          <div class="hud-item blue">
            <label>当前压强 (P)</label>
            <span class="val">{{ pressure.toFixed(2) }} <small>atm</small></span>
          </div>
          <div class="hud-item cyan">
            <label>容器体积 (V)</label>
            <span class="val">{{ volume.toFixed(1) }} <small>L</small></span>
          </div>
          <div class="hud-item orange">
            <label>热力学温度 (T)</label>
            <span class="val">{{ temperature }} <small>K</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240" class="gas-chamber">
          <!-- 气缸本体 -->
          <rect x="120" y="30" width="160" height="180" fill="none" stroke="#e2e8f0" stroke-width="4" rx="4" />
          
          <!-- 分子运动层 -->
          <circle v-for="m in molecules" :key="m.id" 
                  :cx="m.x" :cy="m.y" :r="3" 
                  :fill="moleculeColor" />

          <!-- 活塞组件 -->
          <g :transform="`translate(0, ${pistonYOffset})`">
            <rect x="122" y="32" width="156" height="12" fill="#94a3b8" rx="2" />
            <rect x="195" y="-10" width="10" height="42" fill="#cbd5e1" />
          </g>

          <!-- 压力显示弧 -->
          <path :d="gaugePath" fill="none" stroke="#ef4444" stroke-width="2" opacity="0.3" transform="translate(10, 200)" />
        </svg>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card-grid">
          <div class="control-item">
            <div class="label-row">
              <span class="label">压缩活塞 (体积 V)</span>
              <span class="val">{{ volume.toFixed(1) }} L</span>
            </div>
            <el-slider v-model="volume" :min="20" :max="100" size="small"></el-slider>
          </div>
          <div class="control-item">
            <div class="label-row">
              <span class="label">外部加热 (温度 T)</span>
              <span class="val">{{ temperature }} K</span>
            </div>
            <el-slider v-model="temperature" :min="100" :max="600" size="small"></el-slider>
          </div>
        </div>

        <div class="discovery-card-compact" v-latex>
          <i class="el-icon-info"></i>
          <p><strong>物理规律：</strong>在物质的量 n 不变的情况下，PV/T 为定值。尝试固定温度并压缩体积，你会观察到分子的碰撞频率显著增加，从而导致<strong>压强 P 急剧上升</strong>。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GasLawViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      volume: 80,
      temperature: 300,
      molecules: Array.from({length: 25}, (_, i) => ({
        id: i,
        x: 130 + Math.random()*140,
        y: 80 + Math.random()*100,
        vx: (Math.random()-0.5) * 4,
        vy: (Math.random()-0.5) * 4
      })),
      running: true
    }
  },
  computed: {
    pressure() {
      // P = nRT/V (Simplified factor 0.05)
      return (0.05 * this.temperature) / (this.volume * 0.1);
    },
    pistonYOffset() {
      // Offset from top (30). volume 100 means offset 0, volume 20 means offset 140
      return (1 - this.volume/100) * 160;
    },
    moleculeColor() {
      const t = this.temperature;
      if (t > 450) return '#ef4444';
      if (t < 200) return '#3b82f6';
      return '#06b6d4';
    },
    gaugePath() {
      return `M 0,0 A 40,40 0 0 1 ${this.pressure * 5},0`;
    }
  },
  methods: {
    animate() {
      if (!this.running) return;
      
      const speedScale = Math.sqrt(this.temperature / 300);
      const topBoundary = 30 + this.pistonYOffset + 15;
      const bottomBoundary = 205;
      const leftBoundary = 125;
      const rightBoundary = 275;

      this.molecules.forEach(m => {
        m.x += m.vx * speedScale;
        m.y += m.vy * speedScale;

        // Collision logic
        if (m.x < leftBoundary || m.x > rightBoundary) m.vx *= -1;
        if (m.y < topBoundary || m.y > bottomBoundary) m.vy *= -1;

        // Containment correction
        if (m.y < topBoundary) m.y = topBoundary + 2;
        if (m.x < leftBoundary) m.x = leftBoundary + 2;
        if (m.x > rightBoundary) m.x = rightBoundary - 2;
        if (m.y > bottomBoundary) m.y = bottomBoundary - 2;
      });

      this.frameId = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { 
    this.running = false;
    cancelAnimationFrame(this.frameId); 
  }
}
</script>

<style scoped lang="scss">
.gas-law-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #f0fdf4; color: #16a34a; border: 1px solid #dcfce7; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #ffffff; border-radius: 12px; border: 1.5px solid #e2e8f0; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 6px; z-index: 10;
    .hud-item {
      background: rgba(255, 255, 255, 0.9); border-radius: 6px; border: 1px solid #e2e8f0; padding: 4px 10px;
      label { display: block; font-size: 9px; color: #94a3b8; font-weight: 700; }
      .val { font-size: 13px; color: #1e293b; font-weight: 800; font-family: monospace; small { font-size: 9px; color: #64748b; } }
      &.blue { border-left: 3px solid #3b82f6; }
      &.cyan { border-left: 3px solid #06b6d4; }
      &.orange { border-left: 3px solid #f59e0b; }
    }
  }
}

.gas-chamber { display: block; margin: 0 auto; filter: drop-shadow(0 4px 6px rgba(0,0,0,0.02)); }

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px; background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0;
  .control-item {
    .label-row { display: flex; justify-content: space-between; .label { font-size: 12px; color: #64748b; } .val { font-size: 12px; color: #2563eb; font-weight: 700; } }
  }
}

.discovery-card-compact {
  background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px;
  i { color: #16a34a; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } }
}
</style>
